<template>
  <div class="medium-3 bg-white contact--panel">
    <contact-info
      class="mobile-responsiveness"
      :contact="contact"
      :channel-type="channelType"
      @toggle-panel="onPanelToggle"
    >
      <template #mobile-actions>
        <more-actions
          v-if="isMobileViewport"
          :conversation-id="currentChat.id"
          @open-panel="openPanel"
          @toggle-opportunity-sidebar="$emit('toggle-opportunity-sidebar')"
          @toggle-lia-sidebar="$emit('toggle-lia-sidebar')"
        />
      </template>
    </contact-info>
    <woot-modal
      :show.sync="showAddPopup"
      :show-button-close="false"
      :on-close="hideAddPopup"
    >
      <add-closing-reason
        :inbox="inbox"
        :current-chat="currentChat"
        :options="options"
        @on-close="hideAddPopup"
      />
    </woot-modal>
    <draggable
      :list="conversationSidebarItems"
      :disabled="!dragEnabled"
      animation="200"
      class="list-group"
      ghost-class="ghost"
      handle=".drag-handle"
      @start="dragging = true"
      @end="onDragEnd"
    >
      <transition-group>
        <div
          v-for="element in conversationSidebarItems"
          :key="element.name"
          class="list-group-item"
        >
          <div
            v-if="showSidebarItem && element.name === 'last_csat_notes'"
            class="last-csat-notes"
          >
            <accordion-item
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.LAST_CSAT_NOTES')"
              :is-open="isContactSidebarItemOpen('is_last_csat_notes_open')"
              @click="
                value => toggleSidebarUIState('is_last_csat_notes_open', value)
              "
            >
              <conversation-last-csat-notes
                v-if="currentChat && currentChat.id"
              />
            </accordion-item>
          </div>
          <div
            v-if="showSidebarItem && element.name === 'conversation_actions'"
            class="conversation--actions"
          >
            <accordion-item
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.CONVERSATION_ACTIONS')"
              :is-open="isContactSidebarItemOpen('is_conv_actions_open')"
              :disabled="!canChangeConversationAssignment"
              @click="
                value => toggleSidebarUIState('is_conv_actions_open', value)
              "
            >
              <conversation-action
                v-if="currentChat && currentChat.id"
                :conversation-id="conversationId"
                :inbox-id="inboxId"
                :can-change="canChangeConversationAssignment"
              />
            </accordion-item>
          </div>
          <div
            v-else-if="
              showSidebarItem && element.name === 'conversation_participants'
            "
            class="conversation--actions"
          >
            <accordion-item
              :title="$t('CONVERSATION_PARTICIPANTS.SIDEBAR_TITLE')"
              :is-open="isContactSidebarItemOpen('is_conv_participants_open')"
              @click="
                value =>
                  toggleSidebarUIState('is_conv_participants_open', value)
              "
            >
              <conversation-participant
                :conversation-id="conversationId"
                :inbox-id="inboxId"
              />
            </accordion-item>
          </div>
          <div
            v-else-if="showSidebarItem && element.name === 'conversation_info'"
          >
            <accordion-item
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.CONVERSATION_INFO')"
              :is-open="isContactSidebarItemOpen('is_conv_details_open')"
              compact
              @click="
                value => toggleSidebarUIState('is_conv_details_open', value)
              "
            >
              <conversation-info
                :conversation-attributes="conversationAdditionalAttributes"
                :contact-attributes="contactAdditionalAttributes"
              />
            </accordion-item>
          </div>
          <div
            v-else-if="showSidebarItem && element.name === 'contact_attributes'"
          >
            <accordion-item
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.CONTACT_ATTRIBUTES')"
              :is-open="isContactSidebarItemOpen('is_contact_attributes_open')"
              compact
              @click="
                value =>
                  toggleSidebarUIState('is_contact_attributes_open', value)
              "
            >
              <custom-attributes
                attribute-type="contact_attribute"
                attribute-class="conversation--attribute"
                class="even"
                :contact-id="contact.id"
              />
              <custom-attribute-selector
                attribute-type="contact_attribute"
                :contact-id="contact.id"
              />
            </accordion-item>
          </div>
          <div v-else-if="element.name === 'previous_conversation'">
            <accordion-item
              v-if="contact.id"
              :title="
                $t('CONVERSATION_SIDEBAR.ACCORDION.PREVIOUS_CONVERSATION')
              "
              :is-open="isContactSidebarItemOpen('is_previous_conv_open')"
              @click="
                value => toggleSidebarUIState('is_previous_conv_open', value)
              "
            >
              <contact-conversations
                :contact-id="contact.id"
                :conversation-id="conversationId"
              />
            </accordion-item>
          </div>
          <div v-else-if="element.name === 'conversation_opportunities'">
            <accordion-item
              v-if="showOpportunitiesAccordion"
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.OPPORTUNITIES')"
              :is-open="isContactSidebarItemOpen('is_opportunities_open')"
              @click="
                value => toggleSidebarUIState('is_opportunities_open', value)
              "
            >
              <conversation-opportunities
                :conversation-id="currentChat.primary_key"
              />
            </accordion-item>
          </div>
          <div v-else-if="element.name === 'history_ctwa_campaigns'">
            <accordion-item
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.CLICK_TO_WHATSAPP')"
              :is-open="
                isContactSidebarItemOpen('is_history_ctwa_campaigns_open')
              "
              compact
              @click="
                value =>
                  toggleSidebarUIState('is_history_ctwa_campaigns_open', value)
              "
            >
              <conversation-history-ctwa-campaigns
                :conversation-id="conversationId"
              />
            </accordion-item>
          </div>
          <woot-feature-toggle
            v-else-if="showSidebarItem && element.name === 'macros'"
            feature-key="macros"
          >
            <accordion-item
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.MACROS')"
              :is-open="isContactSidebarItemOpen('is_macro_open')"
              compact
              @click="value => toggleSidebarUIState('is_macro_open', value)"
            >
              <macros-list :conversation-id="conversationId" />
            </accordion-item>
          </woot-feature-toggle>
        </div>
      </transition-group>
    </draggable>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import alertMixin from 'shared/mixins/alertMixin';
import AccordionItem from 'dashboard/components/Accordion/AccordionItem';
import ContactConversations from './ContactConversations.vue';
import ConversationOpportunities from './ConversationOpportunities.vue';
import ConversationAction from './ConversationAction.vue';
import ConversationParticipant from './ConversationParticipant.vue';
import ConversationHistoryCtwaCampaigns from './ConversationHistoryCtwaCampaigns';
import MoreActions from '../../../components/widgets/conversation/MoreActions';
import AddClosingReason from 'dashboard/routes/dashboard/settings/closingreasons/Modal';
import ContactInfo from './contact/ContactInfo';
import ConversationInfo from './ConversationInfo';
import CustomAttributes from './customAttributes/CustomAttributes.vue';
import CustomAttributeSelector from './customAttributes/CustomAttributeSelector.vue';
import draggable from 'vuedraggable';
import uiSettingsMixin from 'dashboard/mixins/uiSettings';
import MacrosList from './Macros/List';
import adminMixin from 'dashboard/mixins/isAdmin';
import agentMixin from 'dashboard/mixins/agentMixin';
import ConversationLastCsatNotes from './ConversationLastCsatNotes.vue';
import viewportMixin from 'shared/mixins/viewportMixin';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

export default {
  components: {
    AccordionItem,
    ContactConversations,
    ConversationOpportunities,
    ContactInfo,
    ConversationInfo,
    CustomAttributes,
    CustomAttributeSelector,
    ConversationAction,
    ConversationParticipant,
    ConversationHistoryCtwaCampaigns,
    draggable,
    MacrosList,
    ConversationLastCsatNotes,
    MoreActions,
    AddClosingReason,
  },
  mixins: [alertMixin, uiSettingsMixin, adminMixin, agentMixin, viewportMixin],
  props: {
    conversationId: {
      type: [Number, String],
      required: true,
    },
    inboxId: {
      type: Number,
      default: undefined,
    },
    onToggle: {
      type: Function,
      default: () => {},
    },
  },
  data() {
    return {
      dragEnabled: true,
      conversationSidebarItems: [],
      dragging: false,
      showAddPopup: false,
      options: {},
    };
  },
  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
      currentUser: 'getCurrentUser',
      currentRole: 'getCurrentRole',
      uiFlags: 'inboxAssignableAgents/getUIFlags',
      integrationsList: 'integrations/getAppIntegrations',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
    }),
    conversationAdditionalAttributes() {
      return this.currentConversationMetaData.additional_attributes || {};
    },
    channelType() {
      return this.currentChat.meta?.channel;
    },
    contact() {
      return this.$store.getters['contacts/getContact'](this.contactId);
    },
    contactAdditionalAttributes() {
      return this.contact.additional_attributes || {};
    },
    contactId() {
      return this.currentChat.meta?.sender?.id;
    },
    currentConversationMetaData() {
      return this.$store.getters[
        'conversationMetadata/getConversationMetadata'
      ](this.conversationId);
    },
    hasContactAttributes() {
      const { custom_attributes: customAttributes } = this.contact;
      return customAttributes && Object.keys(customAttributes).length;
    },
    inbox() {
      return this.$store.getters['inboxes/getInbox'](this.currentChat.inbox_id);
    },
    showSidebarItem() {
      if (
        this.inbox.lock_to_single_conversation === false &&
        this.currentChat.status == 'resolved'
      ) {
        return false;
      }
      return true;
    },
    showOpportunitiesAccordion() {
      return this.isFeatureEnabledonAccount(
        this.currentChat.account_id,
        FEATURE_FLAGS.OPPORTUNITY_PIPELINES
      );
    },
    canChangeConversationAssignment() {
      const canAgentChange = this.agentRules?.hasOwnProperty(
        'change_conversation_assignment'
      )
        ? this.agentRules.change_conversation_assignment === true
        : true;

      return (
        canAgentChange ||
        this.currentUser?.type === 'SuperAdmin' ||
        this.currentRole === 'administrator' ||
        this.currentRole === 'team_supervisor'
      );
    },
  },
  watch: {
    conversationId(newConversationId, prevConversationId) {
      if (newConversationId && newConversationId !== prevConversationId) {
        this.getContactDetails();
      }
    },
    contactId() {
      this.getContactDetails();
    },
  },
  mounted() {
    this.conversationSidebarItems = this.conversationSidebarItemsOrder;
    this.getContactDetails();
    this.$store.dispatch('attributes/get', 0);
    if (!this.integrationsList.length) {
      this.$store.dispatch('integrations/get');
    }
  },
  methods: {
    onPanelToggle() {
      this.onToggle();
    },
    openAddPopup() {
      this.showAddPopup = true;
    },
    hideAddPopup() {
      this.showAddPopup = false;
    },
    openPanel(options) {
      this.options = typeof options === 'undefined' ? {} : options;

      this.openAddPopup();
    },
    getContactDetails() {
      if (this.contactId) {
        this.$store.dispatch('contacts/show', { id: this.contactId });
      }
    },
    getAttributesByModel() {
      if (this.contactId) {
        this.$store.dispatch('contacts/show', { id: this.contactId });
      }
    },
    openTranscriptModal() {
      this.showTranscriptModal = true;
    },
    onDragEnd() {
      this.dragging = false;
      this.updateUISettings({
        conversation_sidebar_items_order: this.conversationSidebarItems,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~dashboard/assets/scss/variables';

.contact--panel {
  background: white;
  border-left: 1px solid var(--color-border);
  font-size: $font-size-small;
  overflow-y: auto;
  overflow: auto;
  position: relative;

  i {
    margin-right: $space-smaller;
  }
}

.list-group {
  .list-group-item {
    background-color: var(--white);
  }
}

::v-deep {
  .contact--profile {
    padding-bottom: var(--space-slab);
    border-bottom: 1px solid var(--color-border);
  }
  .conversation--actions .multiselect-wrap--small {
    .multiselect {
      padding-left: var(--space-medium);
      box-sizing: border-box;
    }
    .multiselect__element {
      span {
        width: 100%;
      }
    }
  }
}

.conversation--labels {
  padding: $space-medium;

  .icon {
    margin-right: $space-micro;
    font-size: $font-size-micro;
    color: #fff;
  }

  .label {
    color: #fff;
    padding: 0.2rem;
  }
}

.contact--mute {
  color: $alert-color;
  display: block;
  text-align: left;
}

.contact--actions {
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.contact-info {
  margin-top: var(--space-two);
}

.mobile-responsiveness {
  @media screen and (max-width: 768px) {
    ::v-deep .user-thumbnail-box {
      display: none;
    }
    ::v-deep .contact--details {
      margin-top: var(--space-normal);
    }
    ::v-deep .contact--name-wrap {
      display: none;
    }
  }
}
</style>
